import { useMemo, useState } from "react";

import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { Box, Collapse, Grid, IconButton, Typography } from "@mui/material";
import dayjs from "dayjs";
import { ValueLabel } from "mui-toolkit/ValueLabel";

import { formatDate, formatPercentage, formatPrice } from "../../../utility/formatters";
import { PrintableTilleyDataGrid } from "../../shared/DataGrid/PrintableTilleyDataGrid";
import { calculateScenarioAmortization, calculateStartingBalance } from "./restructuringHelper";

export const ScenarioAmortizationTable = ({ debtSchedule, scenario, originalAmortization = [] }) => {
  const [isExpanded, setIsExpanded] = useState(true);

  const columns = useMemo(
    () => [
      {
        field: "payment_number",
        headerName: "#",
        width: 80,
        align: "center",
        headerAlign: "center",
      },
      {
        field: "payment_date",
        headerName: "Due Date",
        width: 120,
        valueFormatter: (value) => (value ? formatDate(value) : ""),
      },
      {
        field: "beginning_balance",
        headerName: "Balance",
        flex: 1,
        align: "right",
        headerAlign: "right",
        valueFormatter: (value) => (value ? formatPrice(value, 0) : ""),
        cellClassName: "grid-column-group-a first",
      },
      {
        field: "payment_amount",
        headerName: "Payment",
        flex: 1,
        align: "right",
        headerAlign: "right",
        valueFormatter: (value) => (value ? formatPrice(value, 0) : ""),
        cellClassName: "grid-column-group-a bold-header",
        headerClassName: "bold-header",
      },
      {
        field: "principal_payment",
        headerName: "Principal",
        flex: 1,
        align: "right",
        headerAlign: "right",
        valueFormatter: (value) => (value ? formatPrice(value, 0) : ""),
        cellClassName: "grid-column-group-a",
      },
      {
        field: "interest_payment",
        headerName: "Interest",
        flex: 1,
        align: "right",
        headerAlign: "right",
        valueFormatter: (value) => (value ? formatPrice(value, 0) : ""),
        cellClassName: "grid-column-group-a",
      },
      {
        field: "extra_principal_payment",
        headerName: "Extra Principal",
        flex: 1,
        align: "right",
        headerAlign: "right",
        valueFormatter: (value) => (value ? formatPrice(value, 0) : formatPrice(0, 0)),
        cellClassName: "grid-column-group-a",
      },
      {
        field: "cumulative_principal",
        headerName: "Cumulative Principal",
        flex: 1,
        align: "right",
        headerAlign: "right",
        valueFormatter: (value) => (value ? formatPrice(value, 0) : ""),
        cellClassName: "grid-column-group-b first",
      },
      {
        field: "cumulative_interest",
        headerName: "Cumulative Interest",
        flex: 1,
        align: "right",
        headerAlign: "right",
        valueFormatter: (value) => (value ? formatPrice(value, 0) : ""),
        cellClassName: "grid-column-group-b",
      },
      {
        field: "notes",
        headerName: "Notes",
        width: 100,
        align: "left",
        headerAlign: "left",
        renderCell: (params) => {
          const notes = params.value || "";
          return notes.substring(0, 10);
        },
      },
    ],
    []
  );

  const amortizationResult = useMemo(
    () => calculateScenarioAmortization(debtSchedule, scenario, originalAmortization),
    [debtSchedule, scenario, originalAmortization]
  );

  const { schedule: amortization, scheduledPayment } = amortizationResult;

  const startingBalance = useMemo(
    () =>
      calculateStartingBalance(originalAmortization, scenario?.inception_date, scenario?.additional_principal_amount),
    [originalAmortization, scenario?.inception_date, scenario?.additional_principal_amount]
  );

  // Get frequency display text
  const getFrequencyText = (frequency) => {
    const frequencyMap = {
      Annual: "Annual",
      "Semi-Annual": "Semi-Annual",
      Quarterly: "Quarterly",
      Monthly: "Monthly",
      "Bi-Weekly": "Bi-Weekly",
      Weekly: "Weekly",
    };
    return frequencyMap[frequency] || frequency || "Not set";
  };

  const hasData = amortization && amortization.length > 0;

  return (
    <>
      <Grid container spacing={2} sx={{ mt: 2 }}>
        <Grid item xs={12}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <Typography variant="h6" sx={{ flexGrow: 1 }}>
              New Amortization Scenario
            </Typography>
            <IconButton onClick={() => setIsExpanded(!isExpanded)} size="small">
              {isExpanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </Box>

          <Collapse in={isExpanded}>
            {/* Summary Stats */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} md={6}>
                <ValueLabel label="Starting Balance:" value={formatPrice(startingBalance, 0)} />
                <ValueLabel
                  label="Frequency:"
                  value={getFrequencyText(scenario?.payment_frequency || debtSchedule?.payment_frequency)}
                />
                <ValueLabel
                  label="Inception Date:"
                  value={
                    scenario?.inception_date && dayjs(scenario.inception_date).isValid()
                      ? dayjs(scenario.inception_date).format("MM/DD/YYYY")
                      : "..."
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <ValueLabel
                  label="Annual Interest Rate:"
                  value={formatPercentage(scenario?.interest_rate || debtSchedule?.interest_rate, 2)}
                />
                <ValueLabel
                  label="Scheduled Payment:"
                  value={
                    scheduledPayment !== null && scheduledPayment !== undefined
                      ? formatPrice(scheduledPayment, 0)
                      : "Calculated after scenario set"
                  }
                />
              </Grid>
            </Grid>

            {hasData ? (
              <PrintableTilleyDataGrid
                rows={amortization}
                columns={columns}
                getRowId={(row) => row.id}
                isLoading={false}
                gridKey="scenario-amortization"
                defaultPageSize={100}
                pageSizeOptions={[10, 25, 50, 100]}
                sx={{ width: "100%", boxSizing: "border-box" }}
                autoHeight
              />
            ) : (
              <Box
                sx={{
                  p: 4,
                  textAlign: "center",
                  backgroundColor: "background.paper",
                  borderRadius: 1,
                  border: 1,
                  borderColor: "divider",
                }}
              >
                <Typography variant="body1" color="text.secondary">
                  Please set <strong>Interest Rate</strong>, <strong>Maturity Date</strong>, and{" "}
                  <strong>Payment Frequency</strong> to generate the amortization scenario.
                </Typography>
              </Box>
            )}
          </Collapse>
        </Grid>
      </Grid>
    </>
  );
};
